import { matchPath } from "react-router-dom";
import type { Workspace } from "../types/shell.js";
import { navigationFor } from "./sidebar-nav.js";

import type { ShellBreadcrumb } from "../types/breadcrumbs.js";

const detailRoutes: ReadonlyArray<{
  path: string;
  parent: string;
  labelKey: string;
}> = [
  { path: "/me/assessment/:pageId", parent: "/me", labelKey: "breadcrumbs.assessment" },
  { path: "/me/passport/:pageId", parent: "/me/passport", labelKey: "breadcrumbs.domain" },
  { path: "/me/stamps/:stampId", parent: "/me", labelKey: "breadcrumbs.stamp" },
  { path: "/me/skills/:skillId", parent: "/me", labelKey: "breadcrumbs.skill" },
  { path: "/manage/courses", parent: "/manage", labelKey: "breadcrumbs.courseRanking" },
  { path: "/manage/school/setup", parent: "/manage/school", labelKey: "breadcrumbs.schoolSetup" },
  {
    path: "/admin/situation-cards/:cardId",
    parent: "/admin/situation-cards",
    labelKey: "breadcrumbs.situationCard",
  },
];

function workspaceHome(workspace: Workspace): string {
  if (workspace === "employee") return "/me";
  return workspace === "admin" ? "/admin/situation-cards" : "/manage";
}

export function breadcrumbsFor(
  pathname: string,
  workspace: Workspace,
  search = "",
  mentor = false,
): ShellBreadcrumb[] {
  if (workspace === "employee" && pathname === "/me") {
    const params = new URLSearchParams(search);
    const competency = params.get("competency");
    const skill = params.get("skill");
    const learningRoute: ShellBreadcrumb = { labelKey: "nav.learningRoute" };
    if (competency && skill) {
      return [
        { ...learningRoute, to: "/me?overview=1" },
        {
          labelKey: `startingTeacher.competencies.${competency}.title`,
          to: `/me?competency=${encodeURIComponent(competency)}`,
        },
        { labelKey: `startingTeacher.skills.${skill}.title` },
      ];
    }
    if (competency) {
      return [
        { ...learningRoute, to: "/me?overview=1" },
        { labelKey: `startingTeacher.competencies.${competency}.title` },
      ];
    }
    return [learningRoute];
  }
  const root: ShellBreadcrumb[] =
    workspace === "employee" ? [{ labelKey: "breadcrumbs.employee", to: workspaceHome(workspace) }] : [];
  const items = [
    ...navigationFor(workspace, mentor).flatMap((section) => section.items),
    ...(workspace === "employee"
      ? [
          { to: "/me/passport", labelKey: "nav.passport" },
          { to: "/me/activity", labelKey: "nav.activity" },
          { to: "/me/profile", labelKey: "nav.profile" },
          { to: "/me/onboarding", labelKey: "nav.onboarding" },
        ]
      : []),
  ];
  const detail = detailRoutes.find((route) => matchPath({ path: route.path, end: true }, pathname));
  const destination = detail?.parent ?? pathname;
  const active = items
    .filter((item) => destination === item.to || destination.startsWith(`${item.to}/`))
    .sort((left, right) => right.to.length - left.to.length)[0];

  if (!active) return root.map((crumb) => ({ labelKey: crumb.labelKey }));

  const current: ShellBreadcrumb = { labelKey: active.labelKey };
  if (!detail) return [...root, current];
  return [...root, { ...current, to: active.to }, { labelKey: detail.labelKey }];
}
